import _ from 'lodash';
import { t } from '@/composables/useI18n';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';
import type { FieldErrors } from '@/services/models';

export type ParseApiErrorsParams = {
  error: unknown;
  nested?: boolean;
};

type AxiosStyleError = {
  message?: string;
  response?: { status?: number; data?: { errors?: unknown; message?: string } };
};

const toAxiosStyleError = (err: unknown): AxiosStyleError =>
  typeof err === 'object' && err !== null ? (err as AxiosStyleError) : {};

/** True when parsed errors should not be merged into a form (handled globally or no payload). */
const shouldAbortFieldErrorMerge = (err: unknown): boolean => {
  const e = toAxiosStyleError(err);
  if (
    e.response?.status === 403 ||
    e.response?.status === 400 ||
    e.message === 'Network Error'
  ) {
    return true;
  }
  if (e.response?.status === 409 && showPlanLimitsErrorInToast(err)) {
    return true;
  }
  if (!e.response) {
    return true;
  }
  return false;
};

interface StackNode {
  names: (string | number)[];
  value: unknown;
}

const enum WalkState {
  defaultState = 0,
  concatNumeration = 1,
  appendNumeration = 2,
  skipNode = 3,
  appendStringNodeToEnd = 4,
}

const enum NumerationState {
  default = 0,
  hasNumeration = 1,
}

const enum TranslateMode {
  default = 0,
  awaitTranslation = 1,
}

const walkStateByKey: Record<string, number> = {
  rules: WalkState.concatNumeration,
  paths: WalkState.concatNumeration,
  rows: WalkState.concatNumeration,
  landings: WalkState.appendNumeration,
  offers: WalkState.appendNumeration,
  flowInline: WalkState.skipNode,
  data: WalkState.skipNode,
  queryConditions: WalkState.skipNode,
  VALIDATION_FLOW_PATH_AT_LEAST_ONE_OFFER: -2,
  VALIDATION_FLOW_PATH_AT_LEAST_ONE_LANDING: -2,
};

const sanitizeErrorKey = (value: string): string =>
  value.replace(/[^a-zA-Z_0-9]/g, '_').replace(/_{2,}/g, '_');

/**
 * Flattens nested API `errors` payloads into rows for translation (tree walk).
 */
const flattenErrorTree = (data: unknown, numerate = false): unknown[][] => {
  const rows: unknown[][] = [];
  if (typeof data === 'string' || !data) return rows;

  for (const key of Object.keys(data as object)) {
    const stack: StackNode[] = [{ names: [key], value: (data as Record<string, unknown>)[key] }];

    while (stack.length > 0) {
      const node = stack.pop() as StackNode;
      const value = node.value;

      if (typeof value === 'string') continue;

      if (!Array.isArray(value)) {
        for (const objKey of Object.keys(value as object)) {
          stack.push({
            names: [...node.names, objKey],
            value: (value as Record<string, unknown>)[objKey],
          });
        }
        continue;
      }

      for (const j in value) {
        const curValue = _.get(value, j);
        const numeration: number | string = numerate ? Number(j) + 1 : '';

        if (typeof curValue === 'string') {
          rows.push([...node.names, numeration, curValue]);
        } else if (Array.isArray(curValue)) {
          rows.push([...node.names, numeration, ...curValue]);
        } else if (curValue && typeof curValue === 'object' && 'message' in curValue) {
          rows.push([...node.names, numeration, { ...curValue }]);
        } else if (typeof value === 'object') {
          const cv = curValue as Record<string, unknown> | null | undefined;
          for (const objKey of Object.keys(cv || {})) {
            stack.push({
              names: [...node.names, numeration as string | number, objKey],
              value: cv![objKey],
            });
          }
        }
      }
    }
  }

  return rows;
};

type TranslatedErrorRow = string | { type: string; message: string };

/**
 * Turns flattened rows into user-facing strings or `{ type, message }` when `join: false`.
 */
const translateErrorRows = (
  rows: unknown[][],
  message = '',
  config: {
    state?: Record<string, number>;
    translate?: Record<string, Record<string, string>>;
    join?: boolean;
  } = {},
): TranslatedErrorRow[] => {
  const stateCfg = config.state ?? walkStateByKey;
  const translateCfg = config.translate ?? {};
  const join = config.join ?? true;

  const out: TranslatedErrorRow[] = [];
  const outNumerated: TranslatedErrorRow[] = [];

  for (const row of rows) {
    const parts: string[] = [];
    let translationStack: Record<string, string> | null = null;
    let walk: WalkState = WalkState.defaultState;
    let translateMode = TranslateMode.default;
    let rowHasNumeration = false;

    const filtered = row.filter(Boolean) as unknown[];

    if (join) {
      filtered.forEach((error: unknown) => {
        if (typeof error === 'number') {
          if (walk === WalkState.concatNumeration) {
            parts[parts.length - 1] += ` #${error}`;
            walk = WalkState.defaultState;
            rowHasNumeration = true;
          } else if (walk === WalkState.appendNumeration) {
            walk = WalkState.appendStringNodeToEnd;
            parts.push(`#${error}`);
            parts.push('');
            rowHasNumeration = true;
          }
          return;
        }

        if (typeof error === 'string' || (error && typeof error === 'object' && 'message' in error)) {
          const errorString = typeof error === 'string' ? error : String((error as { message: string }).message);

          if (stateCfg[errorString] === WalkState.skipNode) return;
          if (stateCfg[errorString] < 0) {
            for (let i = stateCfg[errorString]; i < 0; i++) parts.pop();
          }

          const preparedError =
            typeof error === 'object' && error && 'message' in error ? error : sanitizeErrorKey(error as string);
          const field = parts.length ? parts[parts.length - 1] : '';

          let translation: string | null = null;

          const prepKey =
            typeof preparedError === 'string' ? preparedError : String((preparedError as { message: string }).message);

          if (translateCfg[prepKey] && translateMode === TranslateMode.default) {
            translationStack = translateCfg[prepKey];
            translateMode = TranslateMode.awaitTranslation;
          } else if (
            translateMode === TranslateMode.awaitTranslation &&
            translationStack?.[prepKey]
          ) {
            const errObj = error as { message?: string; translated?: boolean };
            translation =
              errObj?.message && errObj?.translated
                ? prepKey
                : t(translationStack[prepKey], [field]);
            translateMode = TranslateMode.default;
          }

          if (!translation) {
            const errObj = error as { message?: string; translated?: boolean };
            const prep =
              typeof preparedError === 'string'
                ? preparedError
                : String((preparedError as { message: string }).message);
            translation =
              errObj?.message && errObj?.translated
                ? errObj.message
                : t(prep, [field]);
          }

          if (walk === WalkState.appendStringNodeToEnd) {
            parts[parts.length - 1] = translation;
          } else {
            parts.push(translation);
            if (stateCfg[error as string]) walk = stateCfg[error as string];
          }
        }
      });
    }

    let item: unknown = parts;

    if (join) {
      item = (parts as string[]).join(' \u2192 ');
    } else {
      const fieldPath = row
        .slice(0, row.length - 1)
        .filter(Boolean)
        .join('.');
      const translatedField = t(fieldPath);
      const last = row[row.length - 1];
      const getMessage = (errorPart: unknown) => {
        if (typeof errorPart === 'string') return t(errorPart, [translatedField]);
        const o = errorPart as { translated?: boolean; message?: string };
        return o?.translated ? o.message! : t(errorPart as string, [t(o?.message!)]);
      };
      item = { type: fieldPath, message: getMessage(last) };
    }

    if (rowHasNumeration) {
      outNumerated.unshift(item as TranslatedErrorRow);
    } else {
      out.push(item as TranslatedErrorRow);
    }
  }

  if (message) out.unshift(t(message));
  return out.concat(outNumerated);
};

const fieldErrorsNested = (errors: unknown): FieldErrors => {
  const parsed = flattenErrorTree(errors);
  const translated = translateErrorRows(parsed, '', { join: false }) as { type: string; message: string }[];
  const result: FieldErrors = {};
  for (const { type, message } of translated) {
    if (!result[type]) result[type] = [];
    result[type].push(message);
  }
  return result;
};

const fieldErrorsFlat = (errors: Record<string, unknown>): FieldErrors => {
  const result: FieldErrors = {};
  for (const field of Object.keys(errors)) {
    const codes = errors[field];
    if (!Array.isArray(codes) || typeof codes[0] !== 'string') continue;
    const code = codes[0];
    const compositeKey = `${field}_${code}`;
    const compositeTranslation = t(compositeKey);
    const compositeKeyNormalized = compositeKey.toLowerCase();
    const looksUntranslated =
      compositeTranslation === compositeKey ||
      compositeTranslation.toLowerCase() === compositeKeyNormalized;
    result[field] = [
      looksUntranslated
        ? `${t(field)} \u2192 ${t(code)}`
        : compositeTranslation,
    ];
  }
  return result;
};

/** Reads `response.data.errors` and builds `FieldErrors` (nested or flat codes). */
const fieldErrorsFromErrorResponse = (err: unknown, nested?: boolean): FieldErrors => {
  const raw = toAxiosStyleError(err).response?.data?.errors;
  if (raw == null) return {};

  if (nested) {
    return fieldErrorsNested(raw);
  }

  if (Array.isArray(raw)) return {};

  try {
    return fieldErrorsFlat(raw as Record<string, unknown>);
  } catch {
    return {};
  }
};

const parseApiErrors = ({ error, nested }: ParseApiErrorsParams): FieldErrors => {
  if (shouldAbortFieldErrorMerge(error)) return {};
  return fieldErrorsFromErrorResponse(error, nested);
};

export const parseFlowErrorsRaw = (errors: unknown): unknown[][] =>
  flattenErrorTree(errors, true);

export const translateFlowErrorsRaw = (
  errorsRaw: unknown[][] | null,
  message = '',
  options: { translate?: Record<string, Record<string, string>> } = {},
): string[] => {
  if (!errorsRaw) return [];
  const sorted = [...errorsRaw].sort(
    (left, right) => Number(left[1]) - Number(right[1]),
  );
  return translateErrorRows(sorted, message, {
    join: true,
    translate: options.translate,
  }) as string[];
};

export const applyFlowSubmitErrorsFromResponse = (error: unknown) => {
  if (shouldAbortFieldErrorMerge(error)) return null;
  const responseData = toAxiosStyleError(error).response?.data;
  if (!responseData?.errors) return null;

  return {
    errorsRaw: parseFlowErrorsRaw(responseData.errors),
    fieldErrors: fieldErrorsNested(responseData.errors),
    errorMessage: responseData.message || '',
  };
};

export default parseApiErrors;
